/**
 * @fileoverview
 * While this is called stateMutators, it's really just a collection of
 * functions that take a state and a component and return a change object.
 *
 * The change object can then be "commit"ed to the state to actually mutate it.
 *
 */

import type { ComponentKey } from "../constants";
import type {
  Change,
  ComponentType,
  Process,
  MegaJSON,
  ReferencedAppSettingsRaw,
  BaseAppSettingsRaw
} from "../types";
import { get as lodashGet } from "lodash-es";
import { checkValidComponentKey, getChangedProperties } from "../utils";
import type { HashMap } from "@certa/types";
import type { DataObjectType } from "@certa/types/src/dataObjects.types";
import type { ReferencedAppSettingsRawTypeMap } from "../types/megaJSON.types";

type ProcessBaseContext = {
  state: MegaJSON;
  processId: string;
  componentKey: ComponentKey;
  component: ComponentType;
};

/**
 * Something new is added to a path that didn't exist before
 * @param props
 * @param props.component The component that is being added
 * @param props.componentKey The key of the component (w.r.t MetaJSON)
 * @returns
 */
const add = ({
  processId,
  component,
  componentKey
}: Omit<ProcessBaseContext, "state">): Change => {
  if (!checkValidComponentKey(componentKey)) {
    throw new Error(`Invalid component key: ${componentKey}`);
  }
  if ("__studioId" in component) {
    return {
      path: `processes.${processId}.${componentKey}.${component.__studioId}`,
      to: component,
      from: undefined
    };
  }
  return {
    path: `processes.${processId}.${componentKey}.${component.id}`,
    to: component,
    from: undefined
  };
};

/**
 * replace or add a entire process in the state
 * @param props
 * @param props.state megaJSON state
 * @param props.processId The id of the process
 * @param props.process The process that is being replaced or added
 * @returns
 */
const replaceProcess = ({
  state,
  processId,
  process
}: {
  state: MegaJSON;
  processId: string;
  process: Process;
}): Change => {
  return {
    path: `processes.${processId}`,
    to: process,
    from: state.processes[processId],
    propertiesChanged: getChangedProperties(state.processes[processId], process)
  };
};

/**
 * Something new is added to a path that didn't exist before
 * @param props
 * @param props.state Current state that is used for getting the current
 *                    value of the component
 * @param props.component The component that is being added
 * @param props.componentKey The key of the component (w.r.t MetaJSON)
 * @returns
 */
const update = ({
  state,
  processId,
  component,
  componentKey
}: ProcessBaseContext): Change => {
  if (!checkValidComponentKey(componentKey)) {
    throw new Error(`Invalid component key: ${componentKey}`);
  }
  const id = "__studioId" in component ? component.__studioId : component.id;
  const current = lodashGet(
    state,
    `processes.${processId}.${componentKey}.${id}`
  );
  return {
    path: `processes.${processId}.${componentKey}.${id}`,
    to: component,
    from: current,
    propertiesChanged: getChangedProperties(current as HashMap, component)
  };
};

/**
 * Something new is added to a path that didn't exist before
 * @param props
 * @param props.state Current state that is used for getting the current
 *                    value of the component
 * @param props.component The component that is being added
 * @param props.componentKey The key of the component (w.r.t MetaJSON)
 * @returns
 */
const remove = ({
  state,
  processId,
  component,
  componentKey
}: ProcessBaseContext): Change => {
  if (!checkValidComponentKey(componentKey)) {
    throw new Error(`Invalid component key: ${componentKey}`);
  }
  const id = "__studioId" in component ? component.__studioId : component.id;
  const current = lodashGet(
    state,
    `processes.${processId}.${componentKey}.${id}`
  );
  return {
    path: `processes.${processId}.${componentKey}.${id}`,
    to: undefined,
    from: current
  };
};

/**
 * Updates the process in the state
 * @param props
 * @param props.state Current state that is used for getting the current
 *                    value of the process
 * @param props.process The process that is being updated
 * @returns
 */
const updateProcess = ({
  processId,
  state,
  process
}: {
  state: MegaJSON;
  processId: string;
  process: Process["process"];
}) => {
  return {
    path: `processes.${processId}.process`,
    to: process,
    // TODO: Check, this is done because it was breaking when megaJSON was initializing
    from: state.processes[processId]?.process,
    propertiesChanged: getChangedProperties(
      state.processes[processId]?.process,
      process
    )
  };
};

/**
 * Updates the appsettings in the state
 * @param props
 * @param props.state Current state that is used for getting the current
 *                    value of the appsettings
 * @param props.appSettings The appsettings that is being updated
 * @returns
 */
const updateAppSettings = ({
  state,
  appSettings
}: {
  state: MegaJSON;
  appSettings: ReferencedAppSettingsRaw;
}) => {
  return {
    path: `app_settings`,
    to: appSettings,
    from: state.app_settings
  } as Change;
};

const updateBaseAppSettings = ({
  state,
  baseAppSettings
}: {
  state: MegaJSON;
  baseAppSettings: BaseAppSettingsRaw;
}) => {
  return {
    path: `base_app_settings`,
    to: baseAppSettings,
    from: state.base_app_settings
  } as Change;
};

/**
 * Adds a new item to an appsettings array
 * @param props
 * @param props.state Current state
 * @param props.appSettingsType The type of appsettings (e.g., 'emailtemplate', 'businessunit')
 * @param props.item The item to add
 * @returns
 */
const addAppSettingsItem = <K extends keyof ReferencedAppSettingsRawTypeMap>({
  state,
  appSettingsType,
  item
}: {
  state: MegaJSON;
  appSettingsType: K;
  item: ReferencedAppSettingsRawTypeMap[K];
}) => {
  const currentArray = [...(state.app_settings?.[appSettingsType] || [])];
  return {
    path: `app_settings.${appSettingsType}`,
    to: [...currentArray, item],
    from: currentArray
  } as Change;
};

/**
 * Updates an item in an appsettings array
 * @param props
 * @param props.state Current state
 * @param props.appSettingsType The type of appsettings (e.g., 'emailtemplate', 'businessunit')
 * @param props.item The item to update
 * @param props.identifier The identifier to find the item (e.g., 'slug', 'code', 'tag')
 * @returns
 */
const updateAppSettingsItem = <
  T extends keyof ReferencedAppSettingsRawTypeMap
>({
  state,
  appSettingsType,
  item,
  identifier
}: {
  state: MegaJSON;
  appSettingsType: T;
  item: ReferencedAppSettingsRawTypeMap[T];
  identifier: keyof ReferencedAppSettingsRawTypeMap[T];
}) => {
  const currentArray: ReferencedAppSettingsRawTypeMap[T][] = Array.isArray(
    state.app_settings?.[appSettingsType]
  )
    ? (state.app_settings?.[
        appSettingsType
      ] as ReferencedAppSettingsRawTypeMap[T][])
    : [];
  const itemIndex = currentArray.findIndex(
    existingItem => existingItem[identifier] === item[identifier]
  );

  if (itemIndex === -1) {
    throw new Error(
      `Item not found in ${appSettingsType} with identifier ${String(identifier)}`
    );
  }

  const updatedArray = [...currentArray];
  updatedArray[itemIndex] = item;

  return {
    path: `app_settings.${appSettingsType}`,
    to: updatedArray,
    from: currentArray
  } as Change;
};

/**
 * Removes an item from an appsettings array
 * @param props
 * @param props.state Current state
 * @param props.appSettingsType The type of appsettings (e.g., 'emailtemplate', 'businessunit')
 * @param props.identifier The identifier to find the item (e.g., 'slug', 'code', 'tag')
 * @param props.identifierValue The value of the identifier
 * @returns
 */
const removeAppSettingsItem = <
  T extends keyof ReferencedAppSettingsRawTypeMap
>({
  state,
  appSettingsType,
  identifier,
  identifierValue
}: {
  state: MegaJSON;
  appSettingsType: T;
  identifier: keyof ReferencedAppSettingsRawTypeMap[T];
  identifierValue: ReferencedAppSettingsRawTypeMap[T][keyof ReferencedAppSettingsRawTypeMap[T]];
}) => {
  const currentArray: ReferencedAppSettingsRawTypeMap[T][] = Array.isArray(
    state.app_settings?.[appSettingsType]
  )
    ? (state.app_settings?.[
        appSettingsType
      ] as ReferencedAppSettingsRawTypeMap[T][])
    : [];
  const updatedArray = currentArray.filter(
    item => item[identifier] !== identifierValue
  );

  return {
    path: `app_settings.${appSettingsType}`,
    to: updatedArray,
    from: currentArray
  } as Change;
};

// Integration tokens/configs are now in regular app settings, not environment-specific
// Removed env-based app settings mutators

export const mutate = {
  add,
  update,
  replaceProcess,
  updateProcess,
  updateAppSettings,
  updateBaseAppSettings,
  addAppSettingsItem,
  updateAppSettingsItem,
  removeAppSettingsItem,
  remove
};

// =============================
// Record (MegaJSON.records) mutators
// =============================
const updateDataObject = ({
  state,
  dataObjectTag,
  dataObject
}: {
  state: MegaJSON;
  dataObjectTag: string;
  dataObject: DataObjectType;
}): Change => {
  const path = `records.${dataObjectTag}`;
  const current: DataObjectType | undefined = lodashGet(state, path);
  return {
    path,
    to: dataObject,
    from: current,
    propertiesChanged:
      typeof dataObject === "object" && typeof current === "object"
        ? getChangedProperties(current, dataObject)
        : []
  } as Change;
};

const replaceDataObjects = ({
  state,
  dataObjects
}: {
  state: MegaJSON;
  dataObjects: Record<string, DataObjectType>;
}): Change => {
  return {
    path: `records`,
    to: dataObjects,
    from: state.records,
    propertiesChanged: getChangedProperties(state.records, dataObjects)
  } as Change;
};

const removeDataObject = ({
  state,
  dataObjectTag
}: {
  state: MegaJSON;
  dataObjectTag: string;
}): Change => {
  return {
    path: `records.${dataObjectTag}`,
    to: undefined,
    from: lodashGet(state, `records.${dataObjectTag}`)
  } as Change;
};

export const mutateDataObjects = {
  updateDataObject,
  removeDataObject,
  replaceDataObjects
};
